import { flatMap, isEmpty, map, orderBy, uniqBy } from "lodash/fp";

import type {
  NoteType,
  PulseClip,
  PulseMetadata,
  PulseResult,
  PulseTopic,
  PulseTopicClip,
  PulseTopicSession,
} from "@app/analytics/Pulse/types";
import type { VideoClip } from "@app/analytics/types";
import ROUTES from "@app/common/routes";
import type {
  ApiPulseMetadata,
  ApiPulseResult,
  ApiPulseTopic,
  Tag,
  UnauthorizedVideoRecording,
  VideoRecording,
} from "@app/common/types";
import { formatTimeTick, splitParagraphs } from "@app/common/utils";
import { withQueryParamsUrl } from "@app/common/utils";
import { EnterpriseWorkspaceTeam } from "@app/common/workspaces/types";
import {
  getDisplayAppUrl,
  optionalParseUTCDateTimeFromString,
  parseUTCDateTimeFromString,
  titleize,
} from "@app/utils/utils";

import { DETAILS_BY_TYPE } from "./constants";

const MIN_CLIP_DURATION = 2;

const bold = (value: string) => `**${value}**`;
const subheading = (value: string) => `*${value}`;
const bullet = (value: string) => `* ${value}`;
const join = (list: string[], lineBreaks: number) =>
  list.join(`${"\n".repeat(lineBreaks)}`);

const getSummary = (topic: PulseTopic) => {
  const paragraphs = splitParagraphs(topic.summary);
  const summaryContent = isEmpty(paragraphs)
    ? "No summary"
    : join(paragraphs, 2);
  return join([bold("For You Summary:"), summaryContent], 1);
};

const getClipLink = (clip: PulseTopicClip) =>
  `${getDisplayAppUrl(
    withQueryParamsUrl(ROUTES.meetingReportById(clip.sessionId), {
      t: clip.timestamp.value,
    })
  )}`;

const getClips = (topic: PulseTopic) => {
  const clipParagraphs = topic.clips.map((clip) => {
    const content = `${clip.text} (Play @ ${
      clip.timestamp.formattedValue
    } ${getClipLink(clip)})`;
    return join([subheading(clip.meetingTitle), content], 1);
  });

  const clipContent = isEmpty(topic.clips)
    ? "No clips"
    : join(clipParagraphs, 2);

  return join([bold("Clips:"), clipContent], 1);
};

const getActionItems = (topic: PulseTopic) => {
  const actionItems = topic.actionItems.map((ai) => bullet(ai.text));
  const actionItemContent = isEmpty(topic.actionItems)
    ? "No action items"
    : join(actionItems, 1);
  return join([bold("Action Items:"), actionItemContent], 1);
};

const getKeyQuestions = (topic: PulseTopic) => {
  const keyQuestions = topic.keyQuestions.map((q) => bullet(q.text));
  const keyQuestionContent = isEmpty(topic.keyQuestions)
    ? "No key questions"
    : join(keyQuestions, 1);
  return join([bold("Key Questions:"), keyQuestionContent], 1);
};

const getContributors = (topic: PulseTopic) => {
  const contributors = topic.contributors;
  const contributorsContent = isEmpty(contributors)
    ? "No contributors"
    : map("name", contributors).join(", ");
  return join([bold("Contributors:"), contributorsContent], 1);
};

export const getModuleCopyContent = (type: NoteType, content: PulseClip[]) => {
  const label = titleize(DETAILS_BY_TYPE[type].label);
  const bulletContent = join(
    content.map((ai) => bullet(ai.text)),
    1
  );
  return join([bold(`${label}:`), bulletContent], 1);
};

/** Generates a markdown version of the topic */
export const getPulseTopicCopyContent = (topic: PulseTopic) => {
  const paragraphs = [
    bold(topic.title),
    getSummary(topic),
    getContributors(topic),
    getClips(topic),
    getActionItems(topic),
    getKeyQuestions(topic),
  ];

  return join(paragraphs, 2);
};

const populateNoteClips = (
  clips: ApiPulseResult["actionItems"]
): PulseClip[] => {
  if (!clips || isEmpty(clips)) return [];

  const populatedClips = clips.map((item) => ({
    id: item.id,
    meetingTitle: item.meetingTitle ?? "",
    ownerUserId: item.ownerUserId,
    sessionId: item.sessionId,
    startTime: optionalParseUTCDateTimeFromString(item.startTime),
    text: item.text,
  }));

  return orderBy(
    (clip) => clip.startTime?.toMillis() ?? 0,
    "desc",
    populatedClips
  );
};

const populatePulseTopicSessions = (sessions: ApiPulseTopic["sessions"]) => {
  const populatedSessions = sessions.map((session) => ({
    ...session,
    meetingStartTime: parseUTCDateTimeFromString(session.meetingStartTime),
  }));
  return orderBy(
    (session) => session.meetingStartTime.toMillis(),
    "desc",
    populatedSessions
  );
};

const populatePulseTopicClips = (
  clips: ApiPulseTopic["excerpts"]
): PulseTopicClip[] => {
  const populatedClips = clips.map((clip) => {
    const startTime = parseUTCDateTimeFromString(clip.startTime);
    const meetingStartTime = parseUTCDateTimeFromString(clip.meetingStartTime);
    const endTime = parseUTCDateTimeFromString(clip.endTime);
    const videoStartTime = optionalParseUTCDateTimeFromString(
      clip.videoStartTime
    );

    const videoOffset = videoStartTime
      ? Math.max(videoStartTime.diff(meetingStartTime).as("seconds"), 0)
      : 0;

    const clipVideoStartTime = startTime.minus({ seconds: videoOffset });
    const clipVideoEndTime = endTime.minus({ seconds: videoOffset });

    const startSeconds = videoStartTime
      ? clipVideoStartTime.diff(videoStartTime).as("seconds")
      : 0;

    const endSeconds = videoStartTime
      ? Math.max(
          clipVideoEndTime.diff(videoStartTime).as("seconds"),
          startSeconds + MIN_CLIP_DURATION
        )
      : 0;

    const duration = videoStartTime
      ? Math.max(endSeconds - startSeconds, MIN_CLIP_DURATION)
      : 0;

    const timestampValue = Math.abs(
      (videoStartTime ?? meetingStartTime).diff(startTime).toMillis()
    );

    return {
      id: clip.id,
      duration,
      end: endSeconds,
      meetingStartTime,
      meetingTitle: clip.meetingTitle ?? "",
      sessionId: clip.sessionId,
      start: startSeconds,
      startTime,
      text: clip.text,
      thumbnailUrl: clip.thumbnailUrl,
      videoStartTime,
      timestamp: {
        value: Math.floor(timestampValue / 1000),
        formattedValue: formatTimeTick(timestampValue),
      },
    };
  });

  return orderBy((clip) => clip.startTime.toMillis(), "desc", populatedClips);
};

const populatePulseTopics = (
  topics: ApiPulseTopic[]
): PulseResult["pulseTopics"] => {
  const populatedTopics = topics.map((topic) => ({
    id: topic.id,
    actionItems: populateNoteClips(topic.actionItems),
    clips: populatePulseTopicClips(topic.excerpts),
    contributors: orderBy("shareOfTopic", "desc", topic.contributors ?? []),
    dateRange: {
      startDate: parseUTCDateTimeFromString(topic.dateRange.start),
      endDate: parseUTCDateTimeFromString(topic.dateRange.end),
      tzName: topic.dateRange.tzName ?? undefined,
    },
    keyQuestions: populateNoteClips(topic.keyQuestions),
    numMeetings: topic.numMeetings,
    shareOfMeetings: Math.round(topic.shareOfMeetings * 100), // turn into percentage
    summary: topic.summary,
    title: topic.title,
    sessions: populatePulseTopicSessions(topic.sessions ?? []),
  }));

  return orderBy("shareOfMeetings", "desc", populatedTopics);
};

/** Transform pulse metadata into preferred shape */
export const parsePulseMetadata = (
  result: ApiPulseMetadata[]
): PulseMetadata[] => {
  const results = result.map((r) => ({
    ...r,
    dateRange: {
      startDate: parseUTCDateTimeFromString(r.dateRange.start),
      endDate: parseUTCDateTimeFromString(r.dateRange.end),
      tzName: r.dateRange.tzName ?? undefined,
    },
  }));

  return orderBy((r) => r.dateRange.startDate.toMillis(), "desc", results);
};

/** Transforms API pulse result into preferred shape */
export const parseSinglePulseResult = (
  result: ApiPulseResult
): PulseResult | null => {
  if (isEmpty(result)) return null;

  return {
    ...result,
    dateRange: {
      startDate: parseUTCDateTimeFromString(result.dateRange.start),
      endDate: parseUTCDateTimeFromString(result.dateRange.end),
      tzName: result.dateRange.tzName ?? undefined,
    },
    actionItems: populateNoteClips(result.actionItems),
    keyQuestions: populateNoteClips(result.keyQuestions),
    pulseTopics: populatePulseTopics(result.pulseTopics),
  };
};

export const getDropdownOptionsById = (pulseResults: PulseMetadata[]) => {
  return pulseResults.map((result) => ({
    value: result.id,
    label: `${result.dateRange.startDate.toLocaleString({
      month: "short",
      day: "numeric",
    })} - ${result.dateRange.endDate.toLocaleString({
      month: "short",
      day: "numeric",
    })}`,
  }));
};

// Map each date to start date so we can easily grab results for the same date ranges across different pulse kinds
export const getDropdownOptionsByDate = (
  pulseMetadataResults: PulseMetadata[]
) =>
  uniqBy(
    "value",
    pulseMetadataResults.map((result) => ({
      value: result.dateRange.startDate.toFormat("yyyy-MM-dd"),
      label: `${result.dateRange.startDate.toLocaleString({
        month: "short",
        day: "numeric",
      })} - ${result.dateRange.endDate.toLocaleString({
        month: "short",
        day: "numeric",
      })}`,
    }))
  );

export const getClip = (
  clip: PulseTopicClip,
  recordingInfo?: VideoRecording | UnauthorizedVideoRecording
): VideoClip | undefined => {
  if (!clip.sessionId || !recordingInfo || recordingInfo.status !== "ready") {
    return;
  }

  return {
    id: clip.id,
    start: clip.start,
    end: clip.end,
    duration: clip.duration,
  };
};

export const getAllContributors = (result?: PulseResult | null) => {
  if (!result) return [];
  return uniqBy("name", flatMap("contributors", result.pulseTopics));
};

export const getAllSessions = (result?: PulseResult | null) => {
  if (!result) return [];
  return orderBy(
    (s: PulseTopicSession) => s.meetingStartTime.toMillis(),
    "desc",
    uniqBy("id", flatMap("sessions", result.pulseTopics))
  );
};

export const resultForEntityAndTimePeriod = (
  selectedEntity: EnterpriseWorkspaceTeam | Tag | null,
  selectedRangeValue: string,
  results: PulseMetadata[],
  selectBy: "tagId" | "teamId"
) => {
  if (!selectedEntity || !selectedRangeValue) return;

  return results.find(
    (result) =>
      result.dateRange.startDate.toFormat("yyyy-MM-dd") ===
        selectedRangeValue && result[selectBy] === selectedEntity.id
  );
};
